import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';

// Hover on web, tap to toggle; dark bubble like the mockup float tip (12px semibold white text, radius 6).
const CHAR_WIDTH = 6.8;
const BUBBLE_PADDING = 22;
const DEFAULT_MAX_WIDTH = 240;

export const estimateBubbleWidth = (text: string, maxWidth: number = DEFAULT_MAX_WIDTH) =>
  Math.min(maxWidth, Math.max(40, Math.round(text.length * CHAR_WIDTH + BUBBLE_PADDING)));

interface WesTooltipTargetProps {
  text: string;
  title?: string;
  markerColor?: string;
  // The target keeps this id; the bubble is `${testID}-tooltip`.
  testID: string;
  children: React.ReactNode | ((state: { open: boolean }) => React.ReactNode);
  style?: StyleProp<ViewStyle>;
  // above/below sit over/under the target; left sits beside it, for cells inside a clipped table.
  placement?: 'above' | 'below' | 'left';
  maxWidth?: number;
  // Offset of the bubble from the target's left edge (above/below); lets a chart keep it inside its card.
  bubbleLeft?: number;
  // Optional control so a chart can keep a single tooltip open at a time.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WesTooltipTarget: React.FC<WesTooltipTargetProps> = ({
  text,
  title,
  markerColor,
  testID,
  children,
  style,
  placement = 'above',
  maxWidth = DEFAULT_MAX_WIDTH,
  bubbleLeft = 0,
  open,
  onOpenChange,
}) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open ?? innerOpen;
  const bubbleWidth = title
    ? Math.max(estimateBubbleWidth(title, maxWidth), estimateBubbleWidth(text, maxWidth - 18) + 18)
    : estimateBubbleWidth(text, maxWidth);
  const setOpen = (next: boolean) => {
    setInnerOpen(next);
    onOpenChange?.(next);
  };

  const position: ViewStyle =
    placement === 'left'
      ? { right: '100%', top: 0, marginRight: 4 }
      : placement === 'below'
        ? { top: '100%', left: bubbleLeft, marginTop: 4 }
        : { bottom: '100%', left: bubbleLeft, marginBottom: 4 };

  return (
    <Pressable
      style={[styles.target, isOpen && styles.targetOpen, style]}
      onPress={() => setOpen(!isOpen)}
      onHoverIn={() => setOpen(true)}
      onHoverOut={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      accessibilityLabel={title ? `${title}: ${text}` : text}
      testID={testID}
    >
      {typeof children === 'function' ? children({ open: isOpen }) : children}
      {isOpen ? (
        <View style={[styles.bubble, title && styles.chartBubble, position, { width: bubbleWidth }]} pointerEvents="none" testID={`${testID}-tooltip`}>
          {title ? <Text style={styles.chartTitle}>{title}</Text> : null}
          <View style={title ? styles.chartBody : undefined}>
            {title && markerColor ? <View style={[styles.chartMarker, { backgroundColor: markerColor, borderColor: markerColor }]} testID={`${testID}-marker`} /> : null}
            <Text style={[styles.bubbleText, title && styles.chartText]}>{text}</Text>
          </View>
          {title && placement !== 'left' ? (
            <View style={[
              styles.chartCaret,
              placement === 'above' ? styles.chartCaretBelow : styles.chartCaretAbove,
              { left: Math.max(6, Math.min(bubbleWidth - 16, 14 - bubbleLeft - 5)) },
            ]} />
          ) : null}
        </View>
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  target: { position: 'relative', overflow: 'visible' },
  targetOpen: { zIndex: 100 },
  bubble: {
    position: 'absolute',
    paddingHorizontal: 11,
    paddingVertical: 7,
    borderRadius: 6,
    backgroundColor: '#2D2D2D',
    shadowColor: '#3A3A3A',
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 4 },
    zIndex: 50,
  },
  bubbleText: { fontSize: 12, lineHeight: 18, fontWeight: '600', fontFamily: FontFamily.medium, color: '#FFFFFF' },
  chartBubble: { paddingHorizontal: 6, paddingVertical: 6, backgroundColor: 'rgba(0, 0, 0, 0.8)', shadowOpacity: 0 },
  chartTitle: { fontSize: 12, lineHeight: 15, fontWeight: '700', fontFamily: FontFamily.bold, color: '#FFFFFF', marginBottom: 6 },
  chartBody: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  chartMarker: { width: 12, height: 12, borderWidth: 2, flexShrink: 0 },
  chartText: { fontWeight: '400', fontFamily: FontFamily.regular, lineHeight: 15, flexShrink: 1 },
  chartCaret: { position: 'absolute', width: 0, height: 0, borderLeftWidth: 5, borderRightWidth: 5, borderLeftColor: 'transparent', borderRightColor: 'transparent' },
  chartCaretBelow: { top: '100%', borderTopWidth: 5, borderTopColor: 'rgba(0, 0, 0, 0.8)' },
  chartCaretAbove: { bottom: '100%', borderBottomWidth: 5, borderBottomColor: 'rgba(0, 0, 0, 0.8)' },
});
